/**
 * Debug infrastructure. The brief was explicit: this won't be one-shot, so make
 * it debuggable. Two things here:
 *   1. A leveled logger (filterable, timestamped, ring-buffered for inspection).
 *   2. A live on-screen debug panel (toggle F3) fed by named "watch" values.
 *
 * Plus a global `window.GAME` handle is attached in main.ts so you can poke at
 * live state from the devtools console.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";
const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

interface LogEntry {
  t: number;
  level: LogLevel;
  tag: string;
  msg: string;
}

class Logger {
  minLevel: LogLevel = "debug";
  private ring: LogEntry[] = [];
  private readonly cap = 500;

  private write(level: LogLevel, tag: string, args: unknown[]) {
    if (LEVEL_ORDER[level] < LEVEL_ORDER[this.minLevel]) return;
    const msg = args
      .map((a) => (typeof a === "string" ? a : safeStringify(a)))
      .join(" ");
    const entry: LogEntry = { t: performance.now(), level, tag, msg };
    this.ring.push(entry);
    if (this.ring.length > this.cap) this.ring.shift();
    const line = `[${tag}] ${msg}`;
    // eslint-disable-next-line no-console
    (console[level === "debug" ? "log" : level] as (...a: unknown[]) => void)(line);
  }

  debug = (tag: string, ...a: unknown[]) => this.write("debug", tag, a);
  info = (tag: string, ...a: unknown[]) => this.write("info", tag, a);
  warn = (tag: string, ...a: unknown[]) => this.write("warn", tag, a);
  error = (tag: string, ...a: unknown[]) => this.write("error", tag, a);

  /** Dump recent log history — handy from the console: GAME.log.history() */
  history = () => [...this.ring];
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export const log = new Logger();

/**
 * Live debug overlay. Systems push values via `watch(key, value)` each frame;
 * the panel renders them. Toggle visibility with F3.
 */
class DebugPanel {
  private el: HTMLDivElement;
  private values = new Map<string, string>();
  visible = false;

  constructor() {
    this.el = document.createElement("div");
    this.el.style.cssText = [
      "position:fixed",
      "top:8px",
      "left:8px",
      "z-index:50",
      "padding:8px 10px",
      "background:rgba(0,0,0,0.72)",
      "color:#5f5",
      "font:12px/1.45 'Courier New',monospace",
      "white-space:pre",
      "pointer-events:none",
      "border:1px solid #2a2a2a",
      "",
      "display:none",
      "max-width:42vw",
    ].join(";");
    document.body.appendChild(this.el);

    window.addEventListener("keydown", (e) => {
      if (e.code === "F3") {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  toggle() {
    this.visible = !this.visible;
    this.el.style.display = this.visible ? "block" : "none";
    log.info("debug", `panel ${this.visible ? "shown" : "hidden"} (F3)`);
  }

  watch(key: string, value: string | number) {
    this.values.set(key, typeof value === "number" ? round2(value).toString() : value);
  }

  /** Called once per frame after systems have reported their watch values. */
  render() {
    if (!this.visible) return;
    let out = "── DEBUG (F3) ──\n";
    for (const [k, v] of this.values) out += `${k.padEnd(14)} ${v}\n`;
    this.el.textContent = out;
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export const debug = new DebugPanel();
